import React, { useState } from 'react';

const IpadBeyondBooks = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const misconceptions = [
    {
      myth: "iPad hanya pengganti buku digital",
      reality: "iPad adalah platform pembelajaran interaktif yang komprehensif",
      icon: "fas fa-book",
      color: "red"
    },
    {
      myth: "Fungsinya sama dengan e-reader biasa",
      reality: "iPad memungkinkan pembelajaran multimedia dan kolaboratif",
      icon: "fas fa-tablet-alt",
      color: "blue"
    },
    {
      myth: "Hanya untuk membaca teks",
      reality: "Mendukung video, audio, simulasi, dan interaksi real-time",
      icon: "fas fa-eye",
      color: "green"
    },
    {
      myth: "Tidak ada nilai tambah dari buku fisik",
      reality: "Memberikan pengalaman belajar yang tidak mungkin dengan buku",
      icon: "fas fa-plus-circle",
      color: "purple"
    }
  ];

  const beyondBooksFeatures = [
    {
      category: "Pembelajaran Interaktif",
      icon: "fas fa-hand-pointer",
      color: "from-blue-500 to-blue-600",
      features: [
        {
          title: "Simulasi 3D dan AR",
          description: "Melihat struktur molekul, sistem tata surya, atau anatomi tubuh dalam 3D",
          example: "Santri bisa 'memegang' dan memutar model Ka'bah 3D untuk memahami geometri dan sejarahnya"
        },
        {
          title: "Video Pembelajaran Interaktif",
          description: "Video yang bisa dijeda, diulang, dan dilengkapi quiz di tengah-tengah",
          example: "Video tajwid dengan deteksi suara untuk koreksi bacaan Al-Qur'an secara real-time"
        },
        {
          title: "Gamifikasi Pembelajaran",
          description: "Belajar melalui permainan edukatif yang menarik dan menantang",
          example: "Game hafalan hadits dengan sistem poin dan leaderboard antar santri"
        }
      ]
    },
    {
      category: "Kolaborasi dan Komunikasi",
      icon: "fas fa-users",
      color: "from-green-500 to-green-600",
      features: [
        {
          title: "Diskusi Real-time",
          description: "Santri bisa berdiskusi dan berbagi ide secara langsung",
          example: "Forum diskusi tafsir ayat dengan moderasi guru dan partisipasi seluruh kelas"
        },
        {
          title: "Proyek Kolaboratif",
          description: "Mengerjakan tugas bersama meski berada di tempat berbeda",
          example: "Membuat majalah digital pesantren dengan kontribusi dari berbagai santri"
        },
        {
          title: "Peer Learning",
          description: "Santri saling mengajar dan berbagi pengetahuan",
          example: "Santri senior membuat video tutorial untuk adik kelas mereka"
        }
      ]
    },
    {
      category: "Personalisasi Pembelajaran",
      icon: "fas fa-user-cog",
      color: "from-purple-500 to-purple-600",
      features: [
        {
          title: "Adaptive Learning",
          description: "Sistem yang menyesuaikan dengan kecepatan belajar masing-masing santri",
          example: "Aplikasi hafalan yang menyesuaikan repetisi berdasarkan kemampuan individual"
        },
        {
          title: "Multi-sensory Learning",
          description: "Pembelajaran yang melibatkan visual, audio, dan kinestetik",
          example: "Belajar sholat dengan video, audio panduan, dan sensor gerak untuk koreksi gerakan"
        },
        {
          title: "Learning Analytics",
          description: "Analisis pola belajar untuk optimalisasi metode pembelajaran",
          example: "Dashboard yang menunjukkan waktu optimal santri untuk menghafal dan memahami materi"
        }
      ]
    },
    {
      category: "Konektivitas dan Akses",
      icon: "fas fa-globe",
      color: "from-orange-500 to-orange-600",
      features: [
        {
          title: "Akses Perpustakaan Global",
          description: "Mengakses ribuan kitab dan referensi dari seluruh dunia",
          example: "Membaca kitab langka dari Perpustakaan Al-Azhar atau Harvard tanpa perlu ke sana"
        },
        {
          title: "Expert Connection",
          description: "Terhubung dengan ulama dan pakar dari berbagai negara",
          example: "Video call dengan ulama Madinah untuk diskusi tafsir atau fiqh kontemporer"
        },
        {
          title: "Real-time Updates",
          description: "Materi pembelajaran selalu ter-update dengan perkembangan terbaru",
          example: "Fatwa terbaru atau perkembangan sains yang relevan dengan ajaran Islam"
        }
      ]
    }
  ];

  const comparisonData = [
    {
      aspect: "Interaktivitas",
      book: "Statis, hanya teks dan gambar",
      ipad: "Dinamis dengan video, audio, simulasi, dan interaksi",
      advantage: "300% lebih engaging"
    },
    {
      aspect: "Personalisasi",
      book: "Sama untuk semua pembaca",
      ipad: "Disesuaikan dengan gaya dan kecepatan belajar individual",
      advantage: "Efektivitas belajar meningkat 250%"
    },
    {
      aspect: "Kolaborasi",
      book: "Pembelajaran individual",
      ipad: "Pembelajaran kolaboratif dan sosial",
      advantage: "Skill teamwork berkembang"
    },
    {
      aspect: "Aksesibilitas",
      book: "Terbatas pada isi buku",
      ipad: "Akses unlimited ke sumber belajar global",
      advantage: "Wawasan 1000x lebih luas"
    },
    {
      aspect: "Feedback",
      book: "Tidak ada feedback langsung",
      ipad: "Feedback real-time dan assessment otomatis",
      advantage: "Koreksi instan, belajar lebih cepat"
    }
  ];

  const realWorldApplications = [
    {
      title: "Pembelajaran Bahasa Arab",
      traditional: "Membaca teks Arab dari buku, menghafal kosakata",
      withIpad: "Aplikasi dengan speech recognition untuk pronunciation, AR untuk menerjemahkan teks real-time, video native speaker",
      impact: "Kemampuan berbicara Arab meningkat 400% lebih cepat"
    },
    {
      title: "Studi Hadits",
      traditional: "Membaca kitab hadits, mencatat sanad dan matan",
      withIpad: "Database hadits dengan pencarian cerdas, verifikasi sanad otomatis, analisis tema, visualisasi jaringan perawi",
      impact: "Pemahaman komprehensif hadits dan konteksnya"
    },
    {
      title: "Pembelajaran Fiqh",
      traditional: "Membaca kitab fiqh klasik, menghafal dalil",
      withIpad: "Simulasi kasus fiqh kontemporer, konsultasi dengan ulama via video, database fatwa terkini",
      impact: "Kemampuan aplikasi fiqh dalam kehidupan modern"
    },
    {
      title: "Sains dan Matematika",
      traditional: "Rumus di buku, contoh soal statis",
      withIpad: "Simulasi eksperimen virtual, kalkulator grafik interaktif, visualisasi konsep abstrak",
      impact: "Pemahaman konsep fundamental meningkat drastis"
    }
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-6">
            <i className="fas fa-tablet-alt text-white text-3xl"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            iPad: Lebih dari Sekadar Pengganti Buku
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Mengapa iPad adalah revolusi pembelajaran, bukan hanya digitalisasi buku
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center mb-8 bg-white rounded-xl shadow-lg p-2">
          {[
            { id: 'overview', label: 'Mitos vs Realita', icon: 'fas fa-eye' },
            { id: 'features', label: 'Fitur Revolusioner', icon: 'fas fa-rocket' },
            { id: 'comparison', label: 'Perbandingan', icon: 'fas fa-balance-scale' },
            { id: 'whyipad', label: 'Mengapa iPad?', icon: 'fas fa-crown' },
            { id: 'applications', label: 'Aplikasi Nyata', icon: 'fas fa-lightbulb' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeSection === tab.id
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Mitos vs Realita */}
        {activeSection === 'overview' && (
          <div className="space-y-12">
            <section className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                Mitos yang Harus Dipatahkan
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {misconceptions.map((item, index) => (
                  <div key={index} className="bg-gradient-to-r from-red-50 to-green-50 rounded-xl p-6 border-l-4 border-red-500">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 bg-${item.color}-500 rounded-full flex items-center justify-center flex-shrink-0`}>
                        <i className={`${item.icon} text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="mb-4">
                          <h3 className="font-bold text-red-600 mb-2">❌ MITOS:</h3>
                          <p className="text-gray-700 italic">"{item.myth}"</p>
                        </div>
                        <div>
                          <h3 className="font-bold text-green-600 mb-2">✅ REALITA:</h3>
                          <p className="text-gray-800 font-medium">{item.reality}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6 text-center">
                Paradigma Baru Pembelajaran Digital
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-6xl mb-4">📚</div>
                  <h3 className="text-xl font-bold mb-2">Era Buku</h3>
                  <p className="opacity-90">Pembelajaran pasif, satu arah, terbatas</p>
                </div>
                <div className="text-center">
                  <div className="text-6xl mb-4">➡️</div>
                  <h3 className="text-xl font-bold mb-2">Transformasi</h3>
                  <p className="opacity-90">Revolusi cara belajar dan mengajar</p>
                </div>
                <div className="text-center">
                  <div className="text-6xl mb-4">📱</div>
                  <h3 className="text-xl font-bold mb-2">Era iPad</h3>
                  <p className="opacity-90">Pembelajaran aktif, interaktif, unlimited</p>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Fitur Revolusioner */}
        {activeSection === 'features' && (
          <div className="space-y-8">
            {beyondBooksFeatures.map((category, categoryIndex) => (
              <section key={categoryIndex} className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${category.color} rounded-full flex items-center justify-center mr-4`}>
                    <i className={`${category.icon} text-white text-2xl`}></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">{category.category}</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {category.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                      <h3 className="text-lg font-bold text-gray-800 mb-3">{feature.title}</h3>
                      <p className="text-gray-600 mb-4">{feature.description}</p>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          <i className="fas fa-lightbulb text-blue-600 mr-2"></i>
                          <strong>Contoh:</strong> {feature.example}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Perbandingan */}
        {activeSection === 'comparison' && (
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Perbandingan Komprehensif: Buku vs iPad
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                    <th className="px-6 py-4 text-left font-bold">Aspek</th>
                    <th className="px-6 py-4 text-left font-bold">📚 Buku Tradisional</th>
                    <th className="px-6 py-4 text-left font-bold">📱 iPad</th>
                    <th className="px-6 py-4 text-left font-bold">🚀 Keunggulan</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-indigo-50 transition-colors`}>
                      <td className="px-6 py-4 font-bold text-gray-800">{row.aspect}</td>
                      <td className="px-6 py-4 text-gray-600">{row.book}</td>
                      <td className="px-6 py-4 text-gray-800 font-medium">{row.ipad}</td>
                      <td className="px-6 py-4">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          {row.advantage}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Mengapa iPad */}
        {activeSection === 'whyipad' && (
          <div className="space-y-8">
            <section className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                <i className="fas fa-crown text-yellow-500 mr-3"></i>
                Mengapa Memilih iPad untuk Digitalisasi Pesantren?
              </h2>
              
              {/* Keunggulan Utama */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border-l-4 border-blue-500">
                    <h3 className="text-xl font-bold text-blue-800 mb-3">
                      <i className="fas fa-shield-alt mr-2"></i>
                      Ekosistem Keamanan Terdepan
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Sistem keamanan berlapis dengan enkripsi hardware dan software yang terintegrasi sempurna.
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Secure Enclave untuk data sensitif</li>
                      <li>• App Store yang dikurasi ketat</li>
                      <li>• Sandboxing aplikasi otomatis</li>
                      <li>• Update keamanan reguler dan cepat</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border-l-4 border-green-500">
                    <h3 className="text-xl font-bold text-green-800 mb-3">
                      <i className="fas fa-cogs mr-2"></i>
                      Manajemen Perangkat Terpadu
                    </h3>
                    <p className="text-gray-700 mb-3">
                      MDM (Mobile Device Management) yang paling matang dan komprehensif di industri.
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Kontrol granular per aplikasi</li>
                      <li>• Deployment massal yang efisien</li>
                      <li>• Monitoring real-time terpusat</li>
                      <li>• Konfigurasi remote yang mudah</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 border-l-4 border-purple-500">
                    <h3 className="text-xl font-bold text-purple-800 mb-3">
                      <i className="fas fa-graduation-cap mr-2"></i>
                      Fokus Pendidikan yang Mendalam
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Dirancang khusus untuk lingkungan pendidikan dengan fitur-fitur yang mendukung pembelajaran.
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• ClassKit untuk integrasi kurikulum</li>
                      <li>• Schoolwork untuk manajemen tugas</li>
                      <li>• Apple Teacher program pelatihan</li>
                      <li>• Aksesibilitas pembelajaran inklusif</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 border-l-4 border-orange-500">
                    <h3 className="text-xl font-bold text-orange-800 mb-3">
                      <i className="fas fa-clock mr-2"></i>
                      Daya Tahan dan Reliabilitas
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Perangkat yang tahan lama dengan dukungan jangka panjang untuk investasi yang berkelanjutan.
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Baterai tahan hingga 10 jam</li>
                      <li>• Build quality premium dan tahan banting</li>
                      <li>• Support OS hingga 5-7 tahun</li>
                      <li>• Performa konsisten jangka panjang</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Perbandingan Kriteria */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                  Kriteria Pemilihan Perangkat untuk Pesantren
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    {
                      title: "Keamanan Data",
                      ipadScore: "95%",
                      otherScore: "70%",
                      icon: "fas fa-lock",
                      color: "blue",
                      reason: "Enkripsi hardware + software terintegrasi"
                    },
                    {
                      title: "Kemudahan Manajemen",
                      ipadScore: "90%",
                      otherScore: "65%",
                      icon: "fas fa-tasks",
                      color: "green",
                      reason: "MDM paling matang dan user-friendly"
                    },
                    {
                      title: "Ekosistem Aplikasi",
                      ipadScore: "88%",
                      otherScore: "75%",
                      icon: "fas fa-store",
                      color: "purple",
                      reason: "App Store terkurasi dengan kualitas tinggi"
                    },
                    {
                      title: "Dukungan Pendidikan",
                      ipadScore: "92%",
                      otherScore: "60%",
                      icon: "fas fa-chalkboard-teacher",
                      color: "orange",
                      reason: "Fitur khusus pendidikan dan program pelatihan"
                    }
                  ].map((criteria, index) => (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300">
                      <div className={`w-12 h-12 bg-${criteria.color}-500 rounded-full flex items-center justify-center mb-4 mx-auto`}>
                        <i className={`${criteria.icon} text-white`}></i>
                      </div>
                      <h4 className="font-bold text-gray-800 text-center mb-3">{criteria.title}</h4>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">iPad:</span>
                          <span className="font-bold text-green-600">{criteria.ipadScore}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Lainnya:</span>
                          <span className="font-bold text-gray-500">{criteria.otherScore}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 text-center italic">{criteria.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Pertimbangan Khusus Pesantren */}
            <section className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Pertimbangan Khusus untuk Lingkungan Pesantren
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="fas fa-mosque text-white text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-blue-800 mb-3 text-center">Nilai-nilai Islami</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Kontrol konten yang ketat sesuai syariat</li>
                    <li>• Filtering otomatis konten tidak pantas</li>
                    <li>• Aplikasi Islami berkualitas tinggi</li>
                    <li>• Jadwal otomatis sesuai waktu ibadah</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="fas fa-users text-white text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-green-800 mb-3 text-center">Manajemen Santri</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Monitoring aktivitas 24/7</li>
                    <li>• Laporan real-time ke wali santri</li>
                    <li>• Kontrol akses berdasarkan waktu</li>
                    <li>• Sistem reward dan punishment digital</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl p-6">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className="fas fa-shield-alt text-white text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-purple-800 mb-3 text-center">Keamanan Maksimal</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Perlindungan dari cyberbullying</li>
                    <li>• Pencegahan akses situs berbahaya</li>
                    <li>• Enkripsi data pribadi santri</li>
                    <li>• Audit trail aktivitas lengkap</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* ROI dan Investasi */}
            <section className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-6 text-center">Return on Investment (ROI)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">
                    <i className="fas fa-chart-line mr-2"></i>
                    Keuntungan Jangka Panjang
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <i className="fas fa-check-circle text-green-300 mt-1"></i>
                      <span>Durabilitas tinggi mengurangi biaya replacement</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="fas fa-check-circle text-green-300 mt-1"></i>
                      <span>Update OS gratis hingga 5-7 tahun</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="fas fa-check-circle text-green-300 mt-1"></i>
                      <span>Resale value yang tinggi</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="fas fa-check-circle text-green-300 mt-1"></i>
                      <span>Efisiensi operasional yang meningkat</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">
                    <i className="fas fa-calculator mr-2"></i>
                    Total Cost of Ownership
                  </h3>
                  <div className="bg-white bg-opacity-20 rounded-lg p-4">
                    <div className="text-center mb-4">
                      <div className="text-3xl font-bold text-yellow-300">35%</div>
                      <div className="text-sm opacity-90">Lebih rendah dalam 5 tahun</div>
                    </div>
                    <ul className="text-sm space-y-2">
                      <li>• Biaya maintenance minimal</li>
                      <li>• Support teknis yang excellent</li>
                      <li>• Training cost yang lebih rendah</li>
                      <li>• Produktivitas yang lebih tinggi</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Aplikasi Nyata */}
        {activeSection === 'applications' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Transformasi Nyata dalam Pembelajaran Pesantren
            </h2>
            {realWorldApplications.map((app, index) => (
              <section key={index} className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">{app.title}</h3>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Traditional Method */}
                  <div className="bg-red-50 rounded-xl p-6 border-l-4 border-red-500">
                    <h4 className="font-bold text-red-600 mb-3">
                      <i className="fas fa-book mr-2"></i>
                      Metode Tradisional
                    </h4>
                    <p className="text-gray-700">{app.traditional}</p>
                  </div>
                  
                  {/* Arrow */}
                  <div className="flex items-center justify-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <i className="fas fa-arrow-right text-white text-xl"></i>
                    </div>
                  </div>
                  
                  {/* iPad Method */}
                  <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-500">
                    <h4 className="font-bold text-green-600 mb-3">
                      <i className="fas fa-tablet-alt mr-2"></i>
                      Dengan iPad
                    </h4>
                    <p className="text-gray-700 mb-4">{app.withIpad}</p>
                    <div className="bg-green-100 rounded-lg p-3">
                      <p className="text-sm text-green-800 font-medium">
                        <i className="fas fa-chart-line mr-2"></i>
                        <strong>Dampak:</strong> {app.impact}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <section className="mt-16">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-6">
              <i className="fas fa-graduation-cap text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold mb-4">Saatnya Melampaui Batas Buku</h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              iPad bukan sekadar pengganti buku - ini adalah gateway menuju pembelajaran abad ke-21 yang interaktif, personal, dan unlimited
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-indigo-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <i className="fas fa-rocket mr-2"></i>
                Mulai Transformasi
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <i className="fas fa-info-circle mr-2"></i>
                Pelajari Lebih Lanjut
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default IpadBeyondBooks;
